import { memo, useId } from 'react';
import type { CollectiblePlugin } from '../../art/content-plugin';
import { heroPluginFor } from '../../art/plugins';
import { heroSkinAssetsFor } from './assets';

/**
 * The HERO content-plugin (ADR-0114) — a thin ADAPTER that exposes a hero through the shared collectible
 * surface (a primary asset + a shop/collection TILE), so anything iterating "all collectibles" treats a hero
 * uniformly with pets + backgrounds. It does NOT re-implement the rich hero rendering: `assetFor` returns the
 * hero's bare BASE figure url (from `heroSkinAssetsFor`, kept for every `skin.tsx`), and `renderTile` draws the
 * figure via the existing `heroPluginFor` registry. A hero is the STAGE SUBJECT, not a staged backdrop, so
 * `renderStage` is omitted. The figure/avatar/scene/itemIcon/forms paths stay on `heroPluginFor` untouched.
 */
const HeroTile = memo(function HeroTile({
  id,
  className,
}: {
  id: string | null;
  className?: string;
}): React.ReactElement {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  return heroPluginFor(id).renderFigure({ uid, stageIndex: 0, look: {}, className });
});

export const heroPlugin: CollectiblePlugin = {
  kind: 'hero',
  assetFor: (id) => heroSkinAssetsFor(id).base || undefined,
  renderTile: ({ id, className }) => <HeroTile id={id} className={className} />,
};
